import {
  Box,
  Button,
  Drawer,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Stack,
  Checkbox,
  ListItemText,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel
} from "@mui/material";
import { COUNTRY_CODE_MAP } from "../../../../constants/constant";

const FilterDrawer = ({
  open,
  onClose,
  filters,
  volumeOptions,
  extensionTypeOptions,
  materialTypeOptions,
  requestStatusOptions,
  countryOptions,
  monthOptions,
  yearOptions,
  handleFilterChange,
  handleMultiSelectChange,
  applyFilters,
  clearFilters,
  areAllFiltersValid,
  isApplyButtonEnabled
}) => {
  const handleApplyFilters = () => {
    const success = applyFilters();
    if (success) {
      onClose();
    }
  };

  // Debug the apply button state
  const applyButtonEnabled = isApplyButtonEnabled();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 350,
          p: 3,
        },
      }}
    >
      <Stack spacing={3}>
        <Typography variant="h6">Advanced Filters</Typography>

        {/* Volume - Radio Buttons */}
        <FormControl component="fieldset">
          <FormLabel component="legend">Volume</FormLabel>
          <RadioGroup
            value={filters.volume}
            onChange={(e) => handleFilterChange('volume', e.target.value)}
            row
          >
            {volumeOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {/* Extension Types - Multi Select */}
        <FormControl fullWidth>
          <InputLabel>Extension Types</InputLabel>
          <Select
            multiple
            value={filters.extensionTypes}
            onChange={(e) => {
              const value = e.target.value;
              if (value.includes('selectAll')) {
                // If "Select All" was clicked
                const allSelected = filters.extensionTypes.length === extensionTypeOptions.length;
                handleMultiSelectChange('extensionTypes', allSelected ? [] : extensionTypeOptions);
              } else {
                handleMultiSelectChange('extensionTypes', value);
              }
            }}
            input={<OutlinedInput label="Extension Types" />}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                  width: 250,
                },
              },
            }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {/* Select All Option */}
            <MenuItem value="selectAll">
              <Checkbox 
                checked={filters.extensionTypes.length === extensionTypeOptions.length && extensionTypeOptions.length > 0}
                indeterminate={filters.extensionTypes.length > 0 && filters.extensionTypes.length < extensionTypeOptions.length}
              />
              <ListItemText primary="Select All" />
            </MenuItem>
            {extensionTypeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={filters.extensionTypes.indexOf(option) > -1} />
                <ListItemText primary={option} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Material Types - Multi Select (Only show when IE is selected) */}
        {filters.extensionTypes.includes('IE') && (
          <FormControl fullWidth>
            <InputLabel>Material Types</InputLabel>
            <Select
              multiple
              value={filters.materialTypes}
              onChange={(e) => {
                const value = e.target.value;
                if (value.includes('selectAll')) {
                  // If "Select All" was clicked
                  const allSelected = filters.materialTypes.length === materialTypeOptions.length;
                  handleMultiSelectChange('materialTypes', allSelected ? [] : materialTypeOptions);
                } else {
                  handleMultiSelectChange('materialTypes', value);
                }
              }}
              input={<OutlinedInput label="Material Types" />}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    width: 250,
                  },
                },
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {/* Select All Option */}
              <MenuItem value="selectAll">
                <Checkbox 
                  checked={filters.materialTypes.length === materialTypeOptions.length && materialTypeOptions.length > 0}
                  indeterminate={filters.materialTypes.length > 0 && filters.materialTypes.length < materialTypeOptions.length}
                />
                <ListItemText primary="Select All" />
              </MenuItem>
              {materialTypeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={filters.materialTypes.indexOf(option) > -1} />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Request Status - Multi Select */}
        <FormControl fullWidth>
          <InputLabel>Request Status</InputLabel>
          <Select
            multiple
            value={filters.requestStatus}
            onChange={(e) => {
              const value = e.target.value;
              if (value.includes('selectAll')) {
                // If "Select All" was clicked
                const allSelected = filters.requestStatus.length === requestStatusOptions.length;
                handleMultiSelectChange('requestStatus', allSelected ? [] : requestStatusOptions);
              } else {
                handleMultiSelectChange('requestStatus', value);
              }
            }}
            input={<OutlinedInput label="Request Status" />}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                  width: 250,
                },
              },
            }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {/* Select All Option */}
            <MenuItem value="selectAll">
              <Checkbox 
                checked={filters.requestStatus.length === requestStatusOptions.length && requestStatusOptions.length > 0}
                indeterminate={filters.requestStatus.length > 0 && filters.requestStatus.length < requestStatusOptions.length}
              />
              <ListItemText primary="Select All" />
            </MenuItem>
            {requestStatusOptions.map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={filters.requestStatus.indexOf(option) > -1} />
                <ListItemText primary={option} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Country - Single Select */}
        <FormControl fullWidth>
          <InputLabel>Country</InputLabel>
          <Select
            value={filters.country}
            onChange={(e) => handleFilterChange('country', e.target.value)}
            label="Country"
          >
            {countryOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {COUNTRY_CODE_MAP[option] || option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Month and Year Selection */}
        <Stack spacing={2}>
          <Typography variant="subtitle2">Time Range</Typography>
          
          {/* Start Range - Year and Month together */}
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Start Range</Typography>
          <Stack direction="row" spacing={1}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={filters.startYear}
                onChange={(e) => handleFilterChange('startYear', e.target.value)}
                label="Year"
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={filters.startMonth || ''}
                onChange={(e) => handleFilterChange('startMonth', e.target.value || null)}
                label="Month"
              >
                {monthOptions.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* End Range - Year and Month together */}
          <Typography variant="body2" sx={{ fontWeight: 'medium', mt: 2 }}>End Range</Typography>
          <Stack direction="row" spacing={1}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={filters.endYear}
                onChange={(e) => handleFilterChange('endYear', e.target.value)}
                label="Year"
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={filters.endMonth || ''}
                onChange={(e) => handleFilterChange('endMonth', e.target.value || null)}
                label="Month"
              >
                {monthOptions.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button 
            variant="contained" 
            onClick={handleApplyFilters} 
            fullWidth
            disabled={!applyButtonEnabled}
            sx={{
              backgroundColor: applyButtonEnabled ? '#ffde00' : '#e0e0e0', // Theme Yellow when enabled, gray when disabled
              color: applyButtonEnabled ? '#1b1c1b' : '#9e9e9e', // Dark text when enabled, gray when disabled
              '&:hover': {
                backgroundColor: applyButtonEnabled ? '#dda002' : '#e0e0e0', // Yellow 75 for hover state when enabled
              },
              '&.Mui-disabled': {
                backgroundColor: '#e0e0e0',
                color: '#9e9e9e',
              }
            }}
          >
            Apply Filters
          </Button>
          <Button variant="outlined" onClick={clearFilters} fullWidth>
            Clear All
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default FilterDrawer;
